import { useState, useEffect, useCallback, memo } from 'react';
import {
  Menu,
  MenuItem,
  ListItemText,
  Radio,
  Button,
  Typography,
  Box
} from '@mui/material';

interface SingleColumnSelectionMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  columns: string[];
  onApply: (selectedColumn: string) => void;
}

// Memoized MenuItem component to prevent unnecessary re-renders
const MemoizedMenuItem = memo(({ 
  column, 
  selected, 
  onSelect 
}: { 
  column: string; 
  selected: boolean; 
  onSelect: () => void;
}) => (
  <MenuItem onClick={onSelect}>
    <Radio checked={selected} />
    <ListItemText primary={column} />
  </MenuItem>
));

const SingleColumnSelectionMenu: React.FC<SingleColumnSelectionMenuProps> = ({
  anchorEl,
  open,
  onClose,
  columns,
  onApply
}) => {
  const [selectedColumn, setSelectedColumn] = useState<string>('');

  // Set default selection when menu opens
  useEffect(() => {
    if (open && columns.length > 0 && !selectedColumn) {
      setSelectedColumn(columns[0]);
    }
  }, [open, columns]);

  const handleColumnSelect = useCallback((column: string) => {
    setSelectedColumn(column);
  }, []);

  const handleApply = useCallback(() => {
    onApply(selectedColumn);
    onClose();
  }, [selectedColumn, onApply, onClose]);

  if (!open) return null;

  return (
  <Menu
    anchorEl={anchorEl}
    open={open}
    onClose={onClose}
    PaperProps={{
      className: "scroll",   
      style: {
        maxHeight: 300,
        width: 'auto',
        overflowY: 'auto',
      },
    }}
  >
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold">Select Column</Typography>
      {columns.map((column) => (
        <MemoizedMenuItem
          key={column}
          column={column}
          selected={selectedColumn === column}
          onSelect={() => handleColumnSelect(column)}
        />
      ))}
    </Box>

    <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
      <Button 
        variant="contained"
        onClick={handleApply}
        disabled={!selectedColumn}
      >
        Apply
      </Button>
    </Box>
  </Menu>
  );
};

export default memo(SingleColumnSelectionMenu); 